// Debounce function (if not already defined)
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';
    this.resultsMaxHeight = 0;

    this.searchButton = this.querySelector('.search__button');

    // Initialize filter elements
    this.filterItems = this.querySelectorAll('.search-filter-item');
    this.resultsWrapper = {
      products: this.querySelector('#predictive-search-results-products-list'),
      collections: this.querySelector('#predictive-search-results-collections-list'),
      blogposts: this.querySelector('#predictive-search-results-blogposts-list'),
      pages: this.querySelector('#predictive-search-results-pages-list'),
    };

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) {
      closeButton.addEventListener('click', this.close.bind(this));
    }

    const resetButton = this.querySelector('button[type="reset"]');
    if (resetButton) {
      resetButton.addEventListener('click', this.clear.bind(this));
    }

    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Event listeners for filter functionality
    this.filterItems.forEach((item) => {
      item.addEventListener('click', this.onFilterClick.bind(this));
    });

    // Event listeners for popular searches
    this.setupPopularSearches();
  }

  setupPopularSearches() {
    // Add event listener to popular searches
    const popularSearchLinks = this.querySelectorAll('.predictive-search__item');
    const searchButton = this.querySelector('.custom_predictive_click');

    if (popularSearchLinks) {
      popularSearchLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();

          const searchTerm = link.getAttribute('data-sorted');

          if (this.input && searchTerm) {
            this.input.value = searchTerm;

            window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
          }
        });
      });
    }

    if (searchButton) {
      searchButton.addEventListener('click', (event) => {
        event.preventDefault();

        const searchTerm = this.input.value;

        if (searchTerm) {
          window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange(event) {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      const groupsWrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (groupsWrapper) groupsWrapper.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear(event);
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange(new Event('input'));
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');

    const currentButtonText = searchForTextElement ? searchForTextElement.innerText : '';
    if (currentButtonText) {
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    this.setLiveRegionText(activeElement.textContent || '');
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    const perPage = this.dataset.perPage || '4';
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    /*     const baseUrl = theme.routes.predictive_search_url;
    const params = [
      `q=${encodeURIComponent(searchTerm)}`,
      `${encodeURIComponent('resources[limit]')}=10`,
      `${encodeURIComponent('resources[limit_scope]')}=each`,
      `${encodeURIComponent('resources[options][fields]')}=author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor`,
      `${encodeURIComponent('resources[type]')}=article`,
      `section_id=predictive-search`,
    ].join('&');

    fetch(`${baseUrl}?${params}`) */

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status.toString());
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const resultsSection = doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsSection ? resultsSection.innerHTML : '';

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);

        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error && error.code === 20) {
          // Code 20 means the call was aborted
          return;
        }

        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');

    // Re-initialize filter elements after rendering new results
    this.filterItems = this.querySelectorAll('.search-filter-item');
    this.resultsWrapper = {
      products: this.querySelector('#predictive-search-results-products-list'),
      collections: this.querySelector('#predictive-search-results-collections-list'),
      blogposts: this.querySelector('#predictive-search-results-blogposts-list'),
      pages: this.querySelector('#predictive-search-results-pages-list'),
    };

    // Set up filter event listeners again
    this.filterItems.forEach((item) => {
      item.addEventListener('click', this.onFilterClick.bind(this));
    });

    // Set initial active filter and display corresponding results
    this.initializeFilters();

    // Re-initialize popular searches event listeners
    this.setupPopularSearches();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(liveRegionCount ? liveRegionCount.textContent || '' : '');
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.header');
    this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event.preventDefault();

    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
  }

  // Filter functionality methods
  onFilterClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    // Remove 'active' class from all filters
    this.filterItems.forEach((item) => item.classList.remove('active'));

    // Add 'active' class to the clicked filter
    target.classList.add('active');

    // Get the selected filter type
    const filterType = target.getAttribute('data-search-filter');

    if (!filterType) return;

    // Show or hide result groups based on the selected filter
    Object.keys(this.resultsWrapper).forEach((key) => {
      if (key === filterType) {
        if (this.resultsWrapper[key]) this.resultsWrapper[key].classList.remove('hidden');
      } else {
        if (this.resultsWrapper[key]) this.resultsWrapper[key].classList.add('hidden');
      }
    });
  }

  initializeFilters() {
    // Initially show only the products list and set the active filter
    /* Object.keys(this.resultsWrapper).forEach((key) => {
      if (key === 'products') {
        if (this.resultsWrapper[key]) this.resultsWrapper[key].classList.remove('hidden');
      } else {
        if (this.resultsWrapper[key]) this.resultsWrapper[key].classList.add('hidden');
      }
    }); */
    /* this.filterItems.forEach((item) => {
      if (item.getAttribute('data-search-filter') === 'products') {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    }); */
  }
}

// Define the custom element
customElements.define('predictive-search', PredictiveSearch);
